import React, { useMemo, useRef, useState, useEffect } from "react";
import { Bold, Italic, Underline, List, ListOrdered } from "lucide-react";

const TextareaField = (props) => {
  const {
    name,
    value,
    onChange,
    label,
    placeholder,
    required,
    minHeight = 120,
    errors,
    enableFormatting = true,
  } = props;

  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      const selection = window.getSelection();
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      const startOffset = range ? range.startOffset : 0;
      const endOffset = range ? range.endOffset : 0;
      
      editorRef.current.innerHTML = value || '';
      
      // Restore cursor position
      if (range && editorRef.current.firstChild) {
        try {
          const newRange = document.createRange();
          const textNode = getTextNodeAtPosition(editorRef.current, startOffset);
          if (textNode) {
            newRange.setStart(textNode.node, Math.min(textNode.offset, textNode.node.textContent.length));
            newRange.setEnd(textNode.node, Math.min(textNode.offset, textNode.node.textContent.length));
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        } catch (e) {
          // Ignore cursor restoration errors
        }
      }
    }
  }, [value]);

  const getTextNodeAtPosition = (root, position) => {
    let currentPosition = 0;
    
    function traverse(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        const length = node.textContent.length;
        if (currentPosition + length >= position) {
          return {
            node: node,
            offset: position - currentPosition
          };
        }
        currentPosition += length;
      } else {
        for (let child of node.childNodes) {
          const result = traverse(child);
          if (result) return result;
        }
      }
      return null;
    }
    
    return traverse(root);
  };

  const handleInput = () => {
    if (editorRef.current) {
      const htmlContent = editorRef.current.innerHTML;
      onChange({ target: { name, value: htmlContent } });
    }
  };

  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
    handleInput();
  };

  const insertList = (ordered = false) => {
    try {
      editorRef.current.focus();
      
      // Use the standard document.execCommand but with fallback
      const command = ordered ? 'insertOrderedList' : 'insertUnorderedList';
      
      // First try the standard command
      if (document.execCommand(command, false, null)) {
        handleInput();
        return;
      }
      
      // Fallback: manual insertion
      const selection = window.getSelection();
      if (selection.rangeCount === 0) return;
      
      const range = selection.getRangeAt(0);
      const listTag = ordered ? 'ol' : 'ul';
      
      // Create a simple list with one item
      const listHTML = `<${listTag}><li>List item</li></${listTag}><p><br></p>`;
      
      // Insert the HTML
      range.deleteContents();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = listHTML;
      
      while (tempDiv.firstChild) {
        range.insertNode(tempDiv.firstChild);
      }
      
      // Move cursor to the list item
      const insertedList = editorRef.current.querySelector(`${listTag}:last-of-type li`);
      if (insertedList) {
        const newRange = document.createRange();
        newRange.selectNodeContents(insertedList);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
      
      handleInput();
    } catch (error) {
      console.error('Error inserting list:', error);
    }
  };

  const formatButtons = [
    {
      icon: Bold,
      label: 'Bold',
      action: () => executeCommand('bold'),
      shortcut: 'Ctrl+B',
      command: 'bold'
    },
    {
      icon: Italic,
      label: 'Italic',
      action: () => executeCommand('italic'),
      shortcut: 'Ctrl+I',
      command: 'italic'
    },
    {
      icon: Underline,
      label: 'Underline',
      action: () => executeCommand('underline'),
      shortcut: 'Ctrl+U',
      command: 'underline'
    },
    {
      icon: List,
      label: 'Bullet List',
      action: () => insertList(false),
      command: 'insertUnorderedList'
    },
    {
      icon: ListOrdered,
      label: 'Numbered List',
      action: () => insertList(true),
      command: 'insertOrderedList'
    },
  ];

  const isCommandActive = (command) => {
    try {
      return document.queryCommandState(command);
    } catch (e) {
      return false;
    }
  };

  const handleKeyDown = (e) => {
    if (!enableFormatting) return;

    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          executeCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          executeCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          executeCommand('underline');
          break;
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    executeCommand('insertText', text);
  };

  const hasError = useMemo(() => {
    return errors?.[name];
  }, [errors, name]);

  return (
    <div className={`w-full ${hasError ? 'text-red-500' : 'text-gray-700'}`}>
      <div className="mb-1 text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </div>
      
      {enableFormatting && (
        <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border border-gray-200 rounded-t-md">
          {formatButtons.map((button, index) => {
            const Icon = button.icon;
            const isActive = isCommandActive(button.command);
            return (
              <button
                key={index}
                type="button"
                onClick={button.action}
                className={`p-1.5 rounded transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  isActive 
                    ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                }`}
                title={`${button.label}${button.shortcut ? ` (${button.shortcut})` : ''}`}
              >
                <Icon size={16} />
              </button>
            );
          })}
        </div>
      )}

      <div
        ref={editorRef}
        contentEditable={true}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={`w-full px-3 py-2 border ${enableFormatting ? 'rounded-b-md border-t-0' : 'rounded-md'} focus:outline-none focus:ring-2 resize-none overflow-auto
          ${hasError
            ? 'border-red-500 focus:ring-red-200'
            : enableFormatting 
              ? 'border-gray-200 focus:ring-blue-200 focus:border-blue-500'
              : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
          }
          text-sm sm:text-base transition-colors duration-200`}
        style={{ 
          minHeight: `${minHeight}px`,
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap'
        }}
        data-placeholder={!value && !isFocused ? placeholder : ''}
        suppressContentEditableWarning={true}
      />
      
      <style jsx>{`
        [contenteditable] ul {
          list-style-type: disc;
          margin-left: 20px;
          padding-left: 0;
        }
        [contenteditable] ol {
          list-style-type: decimal;
          margin-left: 20px;
          padding-left: 0;
        }
        [contenteditable] li {
          margin: 4px 0;
        }
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
      `}</style>
      
      {hasError && (
        <div className="mt-1 text-xs sm:text-sm text-red-500">{errors?.[name]}</div>
      )}
    </div>
  );
};

export default TextareaField ; 